import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import logger from "../utils/logger";

interface UsePipWindowProps {
  isRecording: boolean;
  isPaused: boolean;
  recordingStartTime: number | null;
  showCountdown: boolean;
  webcamStream: MediaStream | null;
  isWebcamOn: boolean;
  isScreenSharing: boolean;
  isUploading: boolean;
  showRetryButton: boolean;
  showStuckChunksOverlay: boolean;
  mirroredWebcam: boolean;
  avatarWebcam: boolean;
  userData?: any;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  deleteRecording: () => void;
  handleStart: () => void;
  currentTimerValue?: string;
  teleprompterData?: {
    text: string;
    currentWordIndex: number;
    isPlaying: boolean;
    highlightSpeed?: number;
    isRTL?: boolean;
    isTeleprompterOpen: boolean;
  };
}

export const usePipWindow = ({
  isRecording,
  isPaused,
  recordingStartTime,
  showCountdown,
  webcamStream,
  isWebcamOn,
  isScreenSharing,
  isUploading,
  showRetryButton,
  showStuckChunksOverlay,
  mirroredWebcam,
  avatarWebcam,
  userData,
  stopRecording,
  pauseRecording,
  resumeRecording,
  deleteRecording,
  handleStart,
  currentTimerValue,
  teleprompterData,
}: UsePipWindowProps) => {
  const pipWindowRef = useRef<Window | null>(null);
  const pipPortRef = useRef<MessagePort | null>(null);
  const prevCurrentWordIndexRef = useRef<number>(-1);
  const [isPipOpen, setIsPipOpen] = useState(false);
  const [isPipReady, setIsPipReady] = useState(false);

  const handleOpenPiP = useCallback(async () => {
    if (!("documentPictureInPicture" in window)) {
      message.error("Document PiP not supported");
      return;
    }

    try {
      // Make the webcam stream accessible to pip.html
      (window as any).sharedStream = webcamStream;

      const pipWindow = await (
        window as any
      ).documentPictureInPicture.requestWindow({
        width: 260,
        height: 240,
      });

      const pipDoc = pipWindow.document;
      const pipURL =
        process.env.NODE_ENV === "production"
          ? `${process.env.REACT_APP_PUBLIC_URL || ""}/pip.html`
          : "/pip.html";
      const pipHTML = await fetch(pipURL).then((r) => r.text());
      pipDoc.open();
      pipDoc.write(pipHTML);
      pipDoc.close();

      // Store the window ref for later
      pipWindowRef.current = pipWindow;
      setIsPipOpen(true);
      setIsPipReady(false);

      // Create MessageChannel for dedicated communication
      const channel = new MessageChannel();
      pipPortRef.current = channel.port1;

      // Set up message handler for the port
      channel.port1.onmessage = (event) => {
        if (event.data?.type === "pip:ready") {
          setIsPipReady(true);

          // Send initial state after handshake
          sendStateUpdate();

          // Send initial teleprompter data if available
          if (teleprompterData) {
            sendTeleprompterData("init");
          }
        } else if (event.data?.type === "pip:closing") {
          cleanupPipConnection();
        }
      };

      // Start the handshake by sending the port to PiP
      pipWindow.postMessage(
        {
          type: "pip:connect",
        },
        "*",
        [channel.port2]
      );

      // Add event listener for when the PiP window is closed manually
      pipWindow.addEventListener("beforeunload", () => {
        cleanupPipConnection();
      });

      pipWindow.addEventListener("resize", () => {
        if (pipPortRef.current && isPipReady) {
          pipPortRef.current.postMessage({
            type: "resize",
          });
        }
      });
    } catch (err) {
      logger.error("❌ Failed to open PiP window:", err);
    }
  }, [
    isPaused,
    isRecording,
    isScreenSharing,
    isUploading,
    isWebcamOn,
    mirroredWebcam,
    avatarWebcam,
    recordingStartTime,
    showCountdown,
    showRetryButton,
    showStuckChunksOverlay,
    webcamStream,
    teleprompterData,
  ]);

  // Helper function to send state updates through the port
  const sendStateUpdate = useCallback(() => {
    if (pipPortRef.current && isPipReady) {
      pipPortRef.current.postMessage({
        type: "stateUpdate",
        state: {
          isRecording,
          isPaused,
          showCountdown,
          isWebcamOn,
          isScreenSharing,
          isUploading,
          showRetryButton,
          showStuckChunksOverlay,
          mirroredWebcam,
          avatarWebcam,
          userData,
          currentTimerValue: currentTimerValue || "00:00",
        },
      });

      // Handle stream availability separately
      if (webcamStream) {
        (window as any).sharedStream = webcamStream;
        pipPortRef.current.postMessage({ type: "streamAvailable" });
      }
    }
  }, [
    isRecording,
    isPaused,
    showCountdown,
    isWebcamOn,
    isScreenSharing,
    isUploading,
    showRetryButton,
    showStuckChunksOverlay,
    mirroredWebcam,
    avatarWebcam,
    userData,
    webcamStream,
    currentTimerValue,
    isPipReady,
  ]);

  // Send recording state to PiP window with batching
  useEffect(() => {
    if (isPipReady) {
      sendStateUpdate();
    }
  }, [sendStateUpdate, isPipReady]);

  // Helper function to send teleprompter data through the port
  const sendTeleprompterData = useCallback(
    (event: string = "init") => {
      if (!pipPortRef.current || !teleprompterData || !isPipReady) return;

      // Send teleprompter data including currentWordIndex
      const teleprompterDataToSend = {
        text: teleprompterData.text,
        isPlaying: teleprompterData.isPlaying,
        highlightSpeed: teleprompterData.highlightSpeed,
        isRTL: teleprompterData.isRTL,
        currentWordIndex: teleprompterData.currentWordIndex,
      };

      pipPortRef.current.postMessage({
        type: `teleprompter:${event}`,
        payload: teleprompterDataToSend,
      });
    },
    [teleprompterData, isPipReady]
  );

  // Send teleprompter data when currentWordIndex or isPlaying changes
  useEffect(() => {
    if (!teleprompterData || !isPipReady) return;

    // Send "playing" when currentWordIndex changes and isPlaying is true
    if (
      teleprompterData.isPlaying &&
      teleprompterData.currentWordIndex !== prevCurrentWordIndexRef.current
    ) {
      sendTeleprompterData("playing");
      prevCurrentWordIndexRef.current = teleprompterData.currentWordIndex;
    }

    if (teleprompterData.isTeleprompterOpen) {
      sendTeleprompterData("init");
    } else {
      sendTeleprompterData("clear");
    }
  }, [
    teleprompterData?.isTeleprompterOpen,
    teleprompterData?.currentWordIndex,
    teleprompterData?.isPlaying,
    sendTeleprompterData,
    isPipReady,
    teleprompterData,
  ]);

  // Send immediate teleprompter play/pause commands
  // useEffect(() => {
  //   if (!pipPortRef.current || !teleprompterData || !isPipReady) return;

  //   // Send immediate play/pause commands without debouncing
  //   if (teleprompterData.isPlaying !== undefined) {
  //     if (teleprompterData.isPlaying) {
  //       pipPortRef.current.postMessage({
  //         type: "teleprompter:play",
  //       });
  //     } else {
  //       pipPortRef.current.postMessage({
  //         type: "teleprompter:pause",
  //       });
  //     }
  //   }
  // }, [teleprompterData?.isPlaying, isPipReady]);

  // Send immediate teleprompter speed changes
  useEffect(() => {
    if (!pipPortRef.current || !teleprompterData || !isPipReady) return;

    // Send immediate speed changes without debouncing
    if (teleprompterData.highlightSpeed !== undefined) {
      pipPortRef.current.postMessage({
        type: "teleprompter:update-speed",
        speed: teleprompterData.highlightSpeed,
      });
    }
  }, [teleprompterData?.highlightSpeed, isPipReady]);

  // Cleanup function for PiP connection
  const cleanupPipConnection = useCallback(() => {
    if (pipPortRef.current) {
      pipPortRef.current.close();
      pipPortRef.current = null;
    }
    pipWindowRef.current = null;
    setIsPipOpen(false);
    setIsPipReady(false);
  }, []);

  // Handle messages from PiP window through the port
  useEffect(() => {
    if (pipPortRef.current) {
      pipPortRef.current.onmessage = async (event: any) => {
        if (event.data?.type === "stopRecording") {
          stopRecording();
          // Send recording stopped message to PiP to reset pause state
          if (pipPortRef.current && isPipReady) {
            pipPortRef.current.postMessage({ type: "recordingStopped" });
          }
        }
        if (event.data?.type === "startRecording") {
          handleStart();
        }
        if (event.data?.type === "pauseRecording") {
          pauseRecording();
        }
        if (event.data?.type === "resumeRecording") {
          resumeRecording();
        }
        if (event.data?.type === "deleteRecording") {
          deleteRecording();
          // Send recording deleted message to PiP to reset UI
          if (pipPortRef.current && isPipReady) {
            pipPortRef.current.postMessage({ type: "recordingDeleted" });
          }
        }
        if (event.data?.type === "exitPIP") {
          try {
            await (
              window as any
            ).documentPictureInPicture?.exitPictureInPicture?.();
            cleanupPipConnection();
          } catch (err) {
            logger.error("Failed to exit PiP:", err);
          }
        }
        if (event.data?.type === "requestTimerValue") {
          if (pipPortRef.current && isPipReady) {
            pipPortRef.current.postMessage({
              type: "currentTimerValue",
              currentTimerValue: currentTimerValue || "00:00",
            });
          }
        }

        // Handle teleprompter word click from PiP window
        if (event.data?.type === "teleprompterWordClick") {
          // Forward to main window for synchronization
          window.postMessage(
            {
              type: "teleprompterWordClick",
              currentWordIndex: event.data.currentWordIndex,
            },
            "*"
          );
        }

        // Handle teleprompter word change from PiP window - sync with main modal
        if (event.data?.type === "teleprompterWordChange") {
          // Forward to main window for synchronization with teleprompter modal
          window.postMessage(
            {
              type: "teleprompterWordChange",
              currentWordIndex: event.data.currentWordIndex,
            },
            "*"
          );
        }
      };
    }
  }, [
    stopRecording,
    pauseRecording,
    resumeRecording,
    deleteRecording,
    handleStart,
    currentTimerValue,
    isPipReady,
    cleanupPipConnection,
  ]);

  // Update PiP when stream availability changes or close if both streams are stopped
  useEffect(() => {
    if (pipWindowRef.current) {
      // Close PiP window if both webcam and screen sharing are stopped
      if (!isWebcamOn && !isScreenSharing) {
        cleanupPipConnection();
        return;
      }

      // The window size is already set correctly in the initial requestWindow call
      // No need to resize here as it maintains the same size regardless of webcam state
      // The button disabled state will be handled by the existing message handlers
      // that send isWebcamOn and isScreenSharing states to the PiP window
    }
  }, [isWebcamOn, isScreenSharing, cleanupPipConnection]);

  // Periodically check if PiP window is still open
  useEffect(() => {
    if (!isPipOpen) return;

    const interval = setInterval(() => {
      if (pipWindowRef.current && pipWindowRef.current.closed) {
        cleanupPipConnection();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPipOpen, cleanupPipConnection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupPipConnection();
    };
  }, [cleanupPipConnection]);

  return {
    handleOpenPiP,
    pipWindowRef,
    isPipOpen,
  };
};
